import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { IRecipient, IsEditViewOnly } from 'views/signed-doc-inbox';
import { EditRecipientFooter, RecipientForm } from './components';
import {
	IEditRecipientProps,
	IsRecipientLoading,
	RecipientErrorsState,
	RecipientLocalState,
	useSubmitRecipient,
} from './stores';
import { useNetwork, useNotification } from 'hooks';
import { API_URL } from 'constant';
import { REACT_APP_PSB_BUSINESSID } from 'envs';
import { loginState } from 'global-stores';

export const EditRecipient: FC<IEditRecipientProps> = ({
	inbox,
	closeModal,
	type,
}) => {
	//local states
	const [newRecipients, setNewRecipients] = useState(inbox.recipients || []);
	const setLocalRecipients = useSetRecoilState(RecipientLocalState);
	const resetErrorRecipients = useResetRecoilState(RecipientErrorsState);
	const updatedRecipient = useRecoilValue(RecipientLocalState);
	const isEditViewOnly = useRecoilValue(IsEditViewOnly);
	const setIsRecipientLoading = useSetRecoilState(IsRecipientLoading);
	const user = useRecoilValue(loginState);
	const { isLoading, showModal, saveRecipient } = useSubmitRecipient();
	const { get } = useNetwork();
	const { errorNotification } = useNotification();

	const handleClose = useCallback(() => {
		closeModal();
	}, [closeModal]);

	const handleSubmit = useCallback(
		() => {
			saveRecipient(updatedRecipient, inbox._id);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[updatedRecipient]
	);
	
	const handleFetchAccessCodes = useCallback(
		async (envelopeId: string) => {
			setIsRecipientLoading(true);
			try {
				const recipientsData = await get(
					`${API_URL.ACCESS_CODE}/${envelopeId}${type === 'session' ? '?type=' + type : ''}`
				);
				if(type === 'session' &&
					recipientsData &&
					recipientsData.message === 'ok' &&
					recipientsData.data
				) {
					// Merge access codes into local recipients
					const mergedRecipients = newRecipients.map(recipient => {
						const found = recipientsData.data;
						return found
							? {
									...recipient,
									accessCode: found.accessCode,
									isAccessCodeEnabled: found.isAccessCodeEnabled,
							  }
							: recipient;
					});
					setNewRecipients(mergedRecipients);
					setLocalRecipients(mergedRecipients);

				}else if (
					recipientsData &&
					recipientsData.message === 'ok' &&
					recipientsData.data &&
					Array.isArray(recipientsData.data.recipients)
				) {
					// Merge access codes into local recipients
					const mergedRecipients = newRecipients.map(recipient => {
						const found = recipientsData.data.recipients.find(
							(r: any) => r._id === recipient._id
						);
						return found
							? {
									...recipient,
									accessCode: found.accessCode,
									isAccessCodeEnabled: found.isAccessCodeEnabled,
							  }
							: recipient;
					});
					setNewRecipients(mergedRecipients);
					setLocalRecipients(mergedRecipients);
				} else {
					errorNotification('Invalid recipients data structure.');
					// Optionally, show an error message to the user here
				}
			} catch (error) {
				errorNotification('Failed to fetch access codes:'+ error);
				// Optionally, show an error message to the user here
			} finally {
				setIsRecipientLoading(false);
			}
		},
		[errorNotification, get, setIsRecipientLoading, newRecipients, setLocalRecipients, type]
	);

	const renderEditForm = useMemo(() => {
		let recipients = inbox.recipients;
		if(REACT_APP_PSB_BUSINESSID === (user?.business || '') ){
			recipients = newRecipients || inbox.recipients;
		}
		return recipients.map((item: IRecipient, index: number) => (
			<RecipientForm recipient={item} index={index} key={item._id} inbox={inbox} type={type}/>
		));
	}, [inbox, newRecipients, user?.business, type]);

	useEffect(() => {
		resetErrorRecipients();
		setLocalRecipients(inbox.recipients);
		handleFetchAccessCodes(inbox._id)
	}, []); // eslint-disable-line

	useEffect(() => {
		if (!showModal) handleClose();
	}, [showModal]); // eslint-disable-line

	const completeStatus = useMemo(() => {
		return inbox.recipients.every((item: IRecipient) =>
			/completed/.test(item.status)
		);
	}, [inbox._id]); // eslint-disable-line

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div className="edit-recipient-header">
				<div>{isEditViewOnly?"Recipients Access Code Information":"Edit recipients information"}</div>
			</div>
			<div className="edit-recipient-container">{renderEditForm}</div>
			<EditRecipientFooter
				primaryBtnLabel={'Save'}
				secondaryBtnLabel="Close"
				handlePrimaryClick={handleSubmit}
				handleSecondaryClick={handleClose}
				loading={isLoading}
				disabled={completeStatus}
				type={type}
			/>
		</div>
	);
};
